import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Loader2,
    FileText,
    ArrowRight,
    ReceiptText,
    Store,
    Download,
    Table as TableIcon,
    HistoryIcon,
    User,
    Calendar,
    Wallet
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchSalesPerformanceAnalysis, fetchSalesPerformanceExportCSV, fetchStoresWhereStaffAssigned } from '../apiHelper';
import Card from '../components/ui/Card';
import Modal from '../components/ui/Modal';
import ThermalReceipt from './ThermalReceipt';
import { useToast } from '../hooks/useToast';

const formatDate = (date) => date.toISOString().split('T')[0];

const SalesReport = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [csvLoading, setCsvLoading] = useState(false);
    const [reportData, setReportData] = useState({ summary: null, orders: [] });

    const [range, setRange] = useState({
        start_date: location.state?.passedStartDate || formatDate(new Date(new Date().setDate(new Date().getDate() - 30))),
        end_date: location.state?.passedEndDate || formatDate(new Date())
    });

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) navigate('/login');
        else setUser(JSON.parse(lcUser));
    }, [navigate]);

    useEffect(() => {
        if (user) {
            fetchStoresWhereStaffAssigned(user).then(allStores => {
                const rtlStores = allStores.filter(s => s.storetype === 'RTL');
                setStores(rtlStores);
                if (rtlStores.length > 0) setSelectedStore(rtlStores[0]);
            });
        }
    }, [user]);

    const loadData = async () => {
        if (!selectedStore) return;
        setLoading(true);
        try {
            const results = await fetchSalesPerformanceAnalysis({ ...range, store_id: selectedStore.store_id });
            setReportData(results);
        } catch (err) {
            showToast("Failed to load transaction details", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [range, selectedStore]);

    const formatFullName = (person) => {
        if (!person) return 'Walk-in';
        const { first_name, middle_name, last_name } = person;
        return [first_name, middle_name, last_name].filter(Boolean).join(' ').trim() || 'Walk-in';
    };

    const getAggregatedProducts = (orders) => {
        const productMap = {};
        orders.forEach(order => {
            order.items?.forEach(item => {
                const name = item.catentry?.productname || "Unknown Product";
                if (!productMap[name]) productMap[name] = { quantity: 0, total: 0 };
                productMap[name].quantity += Number(item.quantity || 0);
                productMap[name].total += Number(item.totalproduct || 0);
            });
        });
        return Object.entries(productMap).map(([name, stats]) => [
            name, stats.quantity.toLocaleString(), stats.total.toLocaleString()
        ]);
    };

    const exportToCSV = async () => {
        setCsvLoading(true);
        try {
            const baseCsvContent = await fetchSalesPerformanceExportCSV({ ...range, store_id: selectedStore.store_id });
            const productRows = getAggregatedProducts(reportData.orders);
            const { summary } = reportData;
            let extraContent = `\n\nFINANCIAL SUMMARY\n`;
            extraContent += `Total Cash Received,₦${Number(summary.total_cash_received).toFixed(2)}\n`;
            extraContent += `Total Credit Extended,₦${Number(summary.total_credit_extended).toFixed(2)}\n`;
            extraContent += `Grand Total (Products),₦${Number(summary.grand_total_product).toFixed(2)}\n`;
            extraContent += `\nPRODUCT SUMMARY\nProduct Name,Quantity Sold,Revenue\n`;
            productRows.forEach(row => { extraContent += `"${row[0]}",${row[1]},${row[2]}\n`; });
            const blob = new Blob([baseCsvContent + extraContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Sales_Report_${selectedStore.identifier}_${range.start_date}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            showToast("CSV exported", "success");
        } catch (err) { showToast("CSV Export failed", 'error'); } finally { setCsvLoading(false); }
    };

    const generatePDF = () => {
        const doc = new jsPDF('l', 'mm', 'a4');
        const { summary, orders } = reportData;
        doc.setFontSize(18);
        doc.text("Detailed Sales Performance Report", 14, 15);
        autoTable(doc, {
            startY: 30,
            head: [['Financial Overview', 'Total Amount (NGN)']],
            body: [
                ['Total Cash Received', Number(summary.total_cash_received).toLocaleString()],
                ['Total Credit Extended', Number(summary.total_credit_extended).toLocaleString()],
                ['Grand Total Revenue', Number(summary.grand_total_product).toLocaleString()]
            ],
            theme: 'grid',
            headStyles: { fillColor: [79, 70, 229] },
            styles: { fontSize: 9, fontStyle: 'bold' },
            margin: { right: 180 }
        });
        const tableRows = orders.map(order => [
            order.orders_id,
            new Date(order.placed_on).toLocaleString(),
            formatFullName(order.customer),
            (Number(order.cash_sale_amount) > 0 && Number(order.credit_sale_amount) > 0) ? 'SPLIT' : (Number(order.credit_sale_amount) > 0 ? 'CREDIT' : 'CASH'),
            Number(order.totalproduct).toLocaleString(),
            Number(order.totaltax).toLocaleString(),
            Number(order.totaldiscount).toLocaleString(),
            Number(order.cash_sale_amount).toLocaleString(),
            Number(order.credit_sale_amount).toLocaleString()
        ]);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 15,
            head: [['ID', 'Date/Time', 'Customer', 'Type', 'Subtotal', 'Tax', 'Disc.', 'Cash', 'Credit']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [31, 41, 55] },
            styles: { fontSize: 8 }
        });
        doc.save(`Sales_Report_${selectedStore?.identifier}.pdf`);
    };

    return (
        <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
            {/* Header Section */}
            <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
                        <HistoryIcon className="text-indigo-600" size={24} /> Sales History
                    </h1>
                    <p className="text-gray-500 text-xs">Comprehensive breakdown of all store movements</p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="flex flex-col sm:flex-row bg-white p-2 rounded-lg border shadow-sm items-center gap-2 sm:gap-4">
                        <div className="flex items-center gap-2 px-2 sm:border-r sm:pr-4 w-full sm:w-auto">
                            <Store size={16} className="text-gray-400" />
                            <select
                                className="text-xs font-bold border-none bg-transparent focus:ring-0 cursor-pointer w-full"
                                value={selectedStore?.store_id || ''}
                                onChange={(e) => setSelectedStore(stores.find(s => s.store_id == e.target.value))}
                            >
                                {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.identifier}</option>)}
                            </select>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-[11px] font-bold w-full sm:w-auto">
                            <input type="date" value={range.start_date} onChange={e => setRange({...range, start_date: e.target.value})} className="border-none p-1 bg-gray-50 rounded w-full sm:w-auto" />
                            <ArrowRight size={14} className="text-gray-300" />
                            <input type="date" value={range.end_date} onChange={e => setRange({...range, end_date: e.target.value})} className="border-none p-1 bg-gray-50 rounded w-full sm:w-auto" />
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button onClick={exportToCSV} disabled={loading || csvLoading} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border px-4 py-2 rounded-lg text-xs font-bold shadow-sm hover:bg-gray-50">
                            {csvLoading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} CSV
                        </button>
                        <button onClick={generatePDF} disabled={loading} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-md hover:bg-indigo-700">
                            <FileText size={14} /> PDF
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile View: Cards */}
            <div className="lg:hidden space-y-4">
                {loading ? (
                    <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-indigo-600" /></div>
                ) : reportData.orders.length === 0 ? (
                    <div className="py-20 text-center text-gray-400 italic bg-white rounded-lg border">No records found</div>
                ) : (
                    reportData.orders.map((order) => {
                        const cashAmt = Number(order.cash_sale_amount || 0);
                        const creditAmt = Number(order.credit_sale_amount || 0);
                        let typeBadge = "bg-emerald-50 text-emerald-700";
                        let typeText = "CASH";
                        if (cashAmt > 0 && creditAmt > 0) { typeBadge = "bg-blue-50 text-blue-700"; typeText = "SPLIT"; }
                        else if (creditAmt > 0) { typeBadge = "bg-orange-50 text-orange-700"; typeText = "CREDIT"; }

                        return (
                            <div key={order.orders_id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <span className="text-[10px] text-gray-400 block mb-1">ORDER ID</span>
                                        <span className="font-bold text-gray-900">#{order.orders_id}</span>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-[10px] font-black ${typeBadge}`}>{typeText}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="flex items-center gap-2">
                                        <Calendar size={14} className="text-gray-400" />
                                        <span className="text-[11px] text-gray-600">{new Date(order.placed_on).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <User size={14} className="text-gray-400" />
                                        <span className="text-[11px] text-gray-600 truncate">{formatFullName(order.customer)}</span>
                                    </div>
                                </div>
                                <div className="border-t pt-3 flex items-center justify-between">
                                    <div>
                                        <span className="text-[10px] text-gray-400 block uppercase">Total Paid</span>
                                        <span className="text-sm font-black text-indigo-600">₦{Number(order.totalproduct).toLocaleString()}</span>
                                    </div>
                                    <button 
                                        onClick={() => { setSelectedOrder(order); setIsModalOpen(true); }} 
                                        className="bg-indigo-50 text-indigo-600 p-2 rounded-lg"
                                    >
                                        <ReceiptText size={18} />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
                {/* Mobile Totals Card */}
                {!loading && reportData.summary && (
                    <div className="bg-gray-800 text-white p-4 rounded-xl shadow-lg mt-6">
                        <div className="flex items-center gap-2 mb-3 border-b border-gray-700 pb-2">
                            <Wallet size={16} className="text-indigo-400" />
                            <span className="text-xs font-bold uppercase tracking-wider">Period Summary</span>
                        </div>
                        <div className="grid grid-cols-2 gap-y-3">
                            <div className="text-[10px] text-gray-400 uppercase">Cash Total</div>
                            <div className="text-xs font-bold text-right text-emerald-400">₦{Number(reportData.summary.total_cash_received).toLocaleString()}</div>
                            <div className="text-[10px] text-gray-400 uppercase">Credit Total</div>
                            <div className="text-xs font-bold text-right text-orange-400">₦{Number(reportData.summary.total_credit_extended).toLocaleString()}</div>
                            <div className="text-[10px] text-gray-100 uppercase font-black border-t border-gray-700 pt-2">Grand Total</div>
                            <div className="text-sm font-black text-right text-white border-t border-gray-700 pt-2">₦{Number(reportData.summary.grand_total_product).toLocaleString()}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Desktop View: Original Table */}
            <Card className="hidden lg:block overflow-hidden border-none shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-800 text-white">
                            <tr>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase">Order ID</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase">Timestamp</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase">Customer</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase">Type</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Subtotal</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Tax</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Discount</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Cash</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Credit</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {loading ? (
                                <tr><td colSpan="10" className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-indigo-600" /></td></tr>
                            ) : reportData.orders.length === 0 ? (
                                <tr><td colSpan="10" className="py-20 text-center text-gray-400 italic">No records found for this period</td></tr>
                            ) : (
                                reportData.orders.map((order) => {
                                    const cashAmt = Number(order.cash_sale_amount || 0);
                                    const creditAmt = Number(order.credit_sale_amount || 0);
                                    let typeBadge = "bg-emerald-50 text-emerald-700";
                                    let typeText = "CASH";
                                    if (cashAmt > 0 && creditAmt > 0) { typeBadge = "bg-blue-50 text-blue-700"; typeText = "SPLIT"; }
                                    else if (creditAmt > 0) { typeBadge = "bg-orange-50 text-orange-700"; typeText = "CREDIT"; }
                                    return (
                                        <tr key={order.orders_id} className="hover:bg-gray-50 text-xs text-gray-700 font-medium">
                                            <td className="px-4 py-3 font-bold">#{order.orders_id}</td>
                                            <td className="px-4 py-3 text-gray-500">{new Date(order.placed_on).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
                                            <td className="px-4 py-3">{formatFullName(order.customer)}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${typeBadge}`}>{typeText}</span>
                                            </td>
                                            <td className="px-4 py-3 text-right font-bold">₦{Number(order.totalproduct).toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right text-gray-400">₦{Number(order.totaltax).toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right text-rose-400">₦{Number(order.totaldiscount).toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right text-emerald-600">₦{cashAmt.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right text-orange-600">₦{creditAmt.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-center">
                                                <button onClick={() => { setSelectedOrder(order); setIsModalOpen(true); }} className="text-indigo-600 hover:text-indigo-900">
                                                    <ReceiptText size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        {!loading && reportData.summary && (
                            <tfoot className="bg-gray-50 font-black border-t-2 border-gray-200">
                                <tr className="text-xs text-gray-900">
                                    <td colSpan="4" className="px-4 py-4 text-right uppercase tracking-wider">Totals</td>
                                    <td className="px-4 py-4 text-right">₦{Number(reportData.summary.grand_total_product).toLocaleString()}</td>
                                    <td className="px-4 py-4 text-right">₦{Number(reportData.summary.grand_total_tax).toLocaleString()}</td>
                                    <td className="px-4 py-4 text-right">₦{Number(reportData.summary.grand_total_discount).toLocaleString()}</td>
                                    <td className="px-4 py-4 text-right">₦{Number(reportData.summary.total_cash_received).toLocaleString()}</td>
                                    <td className="px-4 py-4 text-right">₦{Number(reportData.summary.total_credit_extended).toLocaleString()}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </Card>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Transaction Receipt" size="sm">
                <div className="bg-gray-50 -m-6 p-6">
                    <ThermalReceipt order={selectedOrder} />
                </div>
            </Modal>
        </div>
    );
};

export default SalesReport;